import { getDaysInMonth, addDays, userDate, OwaDate } from 'owa-datetime';
import CalendarEvent, {
    createEmptyCalendarEvent,
} from 'owa-calendar-types/lib/types/CalendarEvent';
import { getUserMailboxInfo, MailboxInfo } from 'owa-client-ids';
import BirthdayEvent from 'owa-service/lib/contract/BirthdayEvent';
import { isDateInDateRange, DateRange, getDateRangeLength } from 'owa-datetime-utils';
import { getAllLockedDateRanges } from '../selectors/eventsCacheSelectors';

/**
 * Converts a Birthday event instance to CalendarEvent
 * @param birthdayEvent The Birthday event (service object) to convert
 * @param mailboxInfo The mailbox info for the calendar event
 * @returns The converted CalendarEvent object
 */
export default function calendarEventFromBirthdayEvent(
    birthdayEvent: BirthdayEvent,
    parentFolderId: string,
    dateRange?: DateRange,
    mailboxInfo?: MailboxInfo
): CalendarEvent {
    // If no mailboxInfo is provided then get the user mailbox info
    mailboxInfo = mailboxInfo || getUserMailboxInfo();

    // if no dateRange is provided then get the longest range of the currently locked dateRange for this calendar
    if (!dateRange) {
        const lockedDateRanges = getAllLockedDateRanges(parentFolderId);
        if (lockedDateRanges && lockedDateRanges.length > 0) {
            dateRange = lockedDateRanges.sort((a, b) => {
                // sort the ranges in descending order
                let length_a = getDateRangeLength(a);
                let length_b = getDateRangeLength(b);
                return length_a > length_b ? -1 : length_a < length_b ? 1 : 0;
            })[0];
        }
    }

    // Create empty instance of CalendarEvent so that mobX doesn't freak out
    let emptyCalendarEvent = createEmptyCalendarEvent();

    // calculate event start date from the birthday
    let start = CalculateCalendarItemStart(birthdayEvent.Birthday, dateRange);

    // Copy then overwrite with proper conversions
    let calendarEvent = {
        ...emptyCalendarEvent,
        ...birthdayEvent,
        ...{
            Subject: birthdayEvent.Name,
            Start: start,
            End: addDays(start, 1),
            IsAllDayEvent: true,
            ParentFolderId: { Id: parentFolderId, mailboxInfo: mailboxInfo },
            ItemId: { Id: birthdayEvent.Id.Id, mailboxInfo: mailboxInfo },
            PersonId: birthdayEvent.PersonId,
            EffectiveRights: {
                Delete: true,
                Modify: true,
                Read: true,
            },
            FreeBusyType: 'Free',
            ReminderIsSet: birthdayEvent.PopupReminderSettings[0].IsReminderSet,
            ReminderMinutesBeforeStart:
                birthdayEvent.PopupReminderSettings[0].ReminderMinutesBeforeStart,
            InboxReminders: birthdayEvent.EmailReminderSettings,
        },
    } as CalendarEvent;

    return calendarEvent ? calendarEvent : null;
}

/**
 * Calculates the start date of the calendar item given the birthday.
 * @param birthday The Birthday
 * @returns The start date
 */
function CalculateCalendarItemStart(birthday: string, dateRange: DateRange): OwaDate {
    let birthdayDate = userDate(birthday);
    let candidate = CalculateBirthdayForYear(birthdayDate, dateRange.start.getFullYear());

    return !isDateInDateRange(dateRange, candidate)
        ? CalculateBirthdayForYear(birthdayDate, dateRange.end.getFullYear())
        : candidate;
}

/**
 * Calculates the birthday given the year.
 * @param birthday The Birthday
 * @param year The year
 * @returns The birthday in the year requested
 */
function CalculateBirthdayForYear(birthday: OwaDate, year: number): OwaDate {
    var daysInMonth = getDaysInMonth(birthday);

    var date = birthday.getUTCDate() > daysInMonth ? daysInMonth : birthday.getUTCDate();

    return userDate(year, birthday.getMonth(), date);
}
